import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { 
  authService, 
  getDeviceToken, 
  User, 
  AuthState, 
  loginStart as apiLoginStart, 
  loginVerify as apiLoginVerify 
} from '../services/authService.ts';

const API_BASE = process.env.REACT_APP_API_URL!;


interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  loginStart: (email: string, password: string) => Promise<{ requiresOtp: boolean; sessionId: string }>;
  loginVerify: (sessionId: string, otp: string) => Promise<{ user: User; token: string }>;
  logout: () => void;
  loading: boolean;
}



const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null
  });

  const [loading, setLoading] = useState(true);
  const logoutTimerRef = useRef<number | null>(null);

  const clearLogoutTimer = useCallback(() => {
    if (logoutTimerRef.current !== null) {
      window.clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  const doLogout = useCallback(() => {
    clearLogoutTimer();
    authService.logout();
    setAuthState({
      isAuthenticated: false,
      user: null
    });
  }, [clearLogoutTimer]);

  const scheduleAutoLogout = useCallback((token?: string) => {
    clearLogoutTimer();
    const expiryMs = authService.getTokenExpiryMs(token);
    if (!expiryMs) return;
    const delay = expiryMs - Date.now();
    if (delay <= 0) {
      doLogout();
      return;
    }
    logoutTimerRef.current = window.setTimeout(() => {
      doLogout();
    }, delay);
  }, [clearLogoutTimer, doLogout]);

  useEffect(() => {
    const originalFetch = window.fetch.bind(window);

    const resolveUrl = (input: RequestInfo | URL): string => {
      if (typeof input === 'string') return input;
      if (input instanceof Request) return input.url;
      if (input instanceof URL) return input.toString();
      return '';
    };

    const isApiRequest = (input: RequestInfo | URL) => {
      const url = resolveUrl(input);
      if (!url) return false;
      if (API_BASE && url.startsWith(API_BASE)) return true;
      return url.includes('/api/');
    };

    window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
      const response = await originalFetch(input, init);

      if (isApiRequest(input) && response.status === 401) {
        doLogout();
      }

      return response;
    }) as typeof window.fetch;

    return () => {
      window.fetch = originalFetch;
    };
  }, [doLogout]);

  // -------------------------------
  // INIT: check existing login
  // -------------------------------
  useEffect(() => {
    const initAuth = async () => {
      const user = authService.getCurrentUser();
      const token = authService.getToken();

      if (!user || !token) {
        doLogout();
        setLoading(false);
        return;
      }

      if (authService.isTokenExpired(token)) {
        doLogout();
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/users/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.status === 401) {
          doLogout();
          return;
        }

        if (response.ok) {
          const profileData = await response.json();
          setAuthState({
            isAuthenticated: true,
            user: { ...user, ...profileData }
          });
        } else {
          setAuthState({
            isAuthenticated: true,
            user
          });
        }

        scheduleAutoLogout(token);
      } catch (err) {
        console.error("INIT AUTH ERROR:", err);
        setAuthState({
          isAuthenticated: true,
          user
        });
        scheduleAutoLogout(token);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [doLogout, scheduleAutoLogout]);


  // -------------------------------
  // OLD LOGIN (admin / trusted device)
  // -------------------------------
  const login = async (email: string, password: string): Promise<void> => {
    setLoading(true);
    try {
      const user = await authService.login(email, password);
      setAuthState({
        isAuthenticated: true,
        user
      });
      scheduleAutoLogout();
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------
  // LOGIN START (2FA)
  // -------------------------------
  const loginStart = async (email: string, password: string) => {
  setLoading(true);

  try {
    const deviceToken = getDeviceToken();

    const response = await fetch(`${API_BASE}/api/auth/login-start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, deviceToken })
    });

    const data = await response.json().catch(() => {
      throw new Error("Ongeldige serverrespons");
    });

    if (!response.ok) {
      throw new Error(data.message || "Login mislukt");
    }

    return data; // { requiresOtp, sessionId }
  } catch (err) {
    console.error("loginStart error:", err);
    throw err;
  } finally {
    setLoading(false);
  }
};


  // -------------------------------
  // LOGIN VERIFY (2FA)
  // -------------------------------
  const loginVerify = async (sessionId: string, otp: string) => {
  setLoading(true);

  try {
    const deviceToken = getDeviceToken();

    const response = await fetch(`${API_BASE}/api/auth/login-verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, otp, deviceToken })
    });

    const data = await response.json().catch(() => {
      throw new Error("Ongeldige serverrespons");
    });

    if (!response.ok) {
      throw new Error(data.message || "Code ongeldig");
    }

    authService.saveUser(data.user, data.token);

    setAuthState({
      isAuthenticated: true,
      user: data.user
    });
    scheduleAutoLogout(data.token);

    return data;
  } catch (err) {
    console.error("loginVerify error:", err);
    throw err;
  } finally {
    setLoading(false);
  }
};

  // -------------------------------
  // LOGOUT
  // -------------------------------
  const logout = () => {
    doLogout();
  };

  // -------------------------------
  // EXPORT VALUE
  // -------------------------------
  const value: AuthContextType = {
    ...authState,
    login,
    loginStart,
    loginVerify,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
